import { useTable } from "@hooks/utils/useTable";
import { TableHeaderCell } from "@interfaces/tables";
import {
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@mui/material";
import Iconify from "@stories/atoms/Iconify/Iconify";
import ListTableHead from "@stories/molecules/ListTable/ListTableHead";
import { ListTableRowMenu } from "@stories/molecules/ListTable/ListTableRowMenu";
import ListTableToolbar from "@stories/molecules/ListTable/ListTableToolbar";
import { NoResultsFoundRow } from "@stories/molecules/ListTable/NoResultsFoundRow";
import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { NoRows } from "@stories/molecules/ListTable/NoRows";
import { DataLoadingRow } from "../ListTable/DataLoadingRow";
import { ProfileLineItem, profileTypeToString } from "@interfaces/profile";

interface UserTableProps {
  loading: boolean;
  profile: ProfileLineItem;
  profiles: ProfileLineItem[];
  handleOpen: () => void;
  handleEditUser: (profile: ProfileLineItem) => void;
  handleDeleteUser: (profile: ProfileLineItem) => void;
}

export const UserTable = ({
  loading,
  profile,
  profiles,
  handleOpen,
  handleEditUser,
  handleDeleteUser,
}: UserTableProps) => {
  const [selectedItem, setSelectedItem] = useState<ProfileLineItem | null>(
    null
  );

  const TABLE_HEAD: TableHeaderCell[] = [
    { id: "first-name", label: "First Name", alignRight: false },
    { id: "last-name", label: "Last Name", alignRight: false },
    { id: "email", label: "Email", alignRight: false },
    { id: "admin", label: "Admin", alignRight: false },
    { id: "" },
  ];

  const {
    anchorElement,
    page,
    selected,
    filterName,
    rowsPerPage,
    handleOpenMenu,
    handleCloseMenu,
    handleSelectAllClick,
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterByName,
  } = useTable();

  const filteredUsers =
    filterName.length > 0
      ? profiles.filter(
          (t) => t.name.toLowerCase() === filterName.toLocaleLowerCase()
        )
      : profiles;

  const isNotFound = !filteredUsers.length && !!filterName;

  const menuItems = [
    {
      name: "Edit",
      color: "black",
      icon: <EditIcon />,
      action: () => {
        if (selectedItem) {
          handleEditUser(selectedItem);
        }
      },
    },
    {
      name: "Archive",
      color: "error.main",
      icon: <DeleteForeverIcon />,
      action: () => {
        if (selectedItem) {
          handleDeleteUser(selectedItem);
        }
      },
    },
  ];

  const handleClose = () => {
    handleCloseMenu();
    setSelectedItem(null);
  };

  return (
    <>
      <Card>
        <ListTableToolbar
          loading={loading}
          handleOpen={handleOpen}
          buttonTitle={"Add User"}
          searchTitle="Search Users..."
          numSelected={selected.length}
          filterName={filterName}
          onFilter={handleFilterByName}
        />

        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <ListTableHead
              headLabel={TABLE_HEAD}
              rowCount={profiles.length}
              numSelected={selected.length}
              showCheckbox={false}
              onSelectAllClick={(e: any) =>
                handleSelectAllClick(
                  e,
                  profiles.map((c) => c.id)
                )
              }
            />
            <TableBody>
              {filteredUsers.map((row) => {
                const { id, name, email, type } = row;
                const selectedUser = selected.indexOf(id) !== -1;

                return (
                  <TableRow
                    hover
                    key={id}
                    tabIndex={-1}
                    role="checkbox"
                    selected={selectedUser}
                  >
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell align="center" component="th" scope="row">
                      {name}
                    </TableCell>
                    <TableCell align="center">{email}</TableCell>
                    <TableCell align="center">
                      {profileTypeToString(type)}
                    </TableCell>
                    <TableCell align="right">
                      {id === profile.id ? undefined : (
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={(event) => {
                            setSelectedItem(row);
                            handleOpenMenu(event);
                          }}
                        >
                          <Iconify icon={"eva:more-vertical-fill"} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {isNotFound && (
              <NoResultsFoundRow
                colSpan={TABLE_HEAD.length + 1}
                filterName={"filterName"}
              />
            )}
            {profiles.length === 0 && !loading ? (
              <NoRows colSpan={TABLE_HEAD.length + 1} title={"Users"} />
            ) : null}
            {loading && <DataLoadingRow colSpan={TABLE_HEAD.length + 1} />}
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={profiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Card>
      <ListTableRowMenu
        menuItems={menuItems}
        anchorElement={anchorElement}
        handleCloseMenu={handleClose}
      />
    </>
  );
};
